/* ==========================================================================
   CHLOIA — Hero slider
   Scroll-snap track (native swipe) + arrows, dots, autoplay.
   Autoplay pauses on hover, focus, hidden tab, and reduced-motion.
   ========================================================================== */
(() => {
  if (customElements.get('ch-hero-slider')) return;

  class ChHeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ch-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-ch-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-ch-slider-dot]'));
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;
      this.style.setProperty('--hs-interval', `${this.interval}ms`);

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('scrollend', () => this.settle(), { passive: true });

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ch-slider-prev]')) this.go(this.index - 1, true);
        else if (e.target.closest('[data-ch-slider-next]')) this.go(this.index + 1, true);
        else {
          const dot = e.target.closest('[data-ch-slider-dot]');
          if (dot) this.go(parseInt(dot.dataset.chSliderDot, 10), true);
        }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (e.key === 'ArrowRight') this.go(this.index + 1, true);
      });

      // Pause while the shopper is looking or interacting
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());
      this.track.addEventListener('touchstart', () => this.pause(), { passive: true });
      this.track.addEventListener('touchend', () => this.resume(), { passive: true });
      this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Only rotate while the banner is on screen
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          if (this.inView) this.resume();
          else this.pause();
        });
        this.io.observe(this);
      } else {
        this.inView = true;
      }

      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    go(i, fromUser = false) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      // Our own animated scroll: ignore scroll events until it settles
      this.programmatic = true;
      clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => (this.programmatic = false), 900);
      this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: this.reduced ? 'auto' : 'smooth' });
      this.update();
      if (fromUser) this.restart();
    }

    // Swipes: read the slide only once scrolling has settled
    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.settle(), 140);
    }

    settle() {
      const w = this.track.clientWidth || 1;
      const i = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / w)));
      if (this.programmatic) {
        if (i === this.index) {
          this.programmatic = false;
          clearTimeout(this.programmaticTimer);
        }
        return;
      }
      if (i !== this.index) {
        this.index = i;
        this.update();
        this.restart();
      }
    }

    update() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => (el.tabIndex = active ? 0 : -1));
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        // restart the progress fill
        const fill = dot.firstElementChild;
        if (fill && active) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });
    }

    start() {
      if (!this.autoplay) return;
      this.stop();
      this.classList.add('is-playing');
      this.classList.remove('is-paused');
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      if (!this.autoplay || this.paused) return;
      this.start();
      this.update();
    }

    pause() {
      if (!this.autoplay) return;
      this.paused = true;
      this.stop();
      this.classList.add('is-paused');
    }

    resume() {
      if (!this.autoplay || this.inView === false || document.hidden) return;
      this.paused = false;
      this.start();
      this.update();
    }
  }

  customElements.define('ch-hero-slider', ChHeroSlider);

  // Theme editor: jump to the selected slide and hold it there
  document.addEventListener('shopify:block:select', (e) => {
    const slider = e.target.closest && e.target.closest('ch-hero-slider');
    if (!slider || !slider.slides) return;
    const i = slider.slides.indexOf(e.target);
    if (i > -1) {
      slider.pause();
      slider.go(i);
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const slider = e.target.closest && e.target.closest('ch-hero-slider');
    if (slider && slider.resume) slider.resume();
  });
})();
